/**
 * Form fields that are always labelled.
 *
 * The design system's field components render the label and the control, but associating
 * them is the caller's job: the label is bound to whatever `labelFor` names, and the
 * control carries whatever `id` the caller gives it. Supply neither and you get a visible
 * label floating beside an unnamed control — which looks completely correct, and is
 * unusable by anybody navigating with a screen reader, because the control announces only
 * its role.
 *
 * That is a mistake with no visual symptom, which makes it the worst kind to leave to
 * per-call discipline. These wrappers generate the identifier and wire both ends, so the
 * association cannot be forgotten. Every screen uses these rather than the raw fields.
 */

import {
  SelectField,
  TextField,
  TextareaField,
} from '@wk/components-v3-react16'
import { useId, type ChangeEvent, type ReactNode } from 'react'

export interface LabelledFieldProps {
  readonly label: string
  readonly description?: string
  readonly errors?: readonly string[]
  readonly size?: 'medium' | 'large'
}

function statusFor(errors: readonly string[] | undefined): 'none' | 'error' {
  return errors !== undefined && errors.length > 0 ? 'error' : 'none'
}

export interface SelectInputProps extends LabelledFieldProps {
  readonly value: string
  readonly onChange: (value: string) => void
  readonly children: ReactNode
  readonly disabled?: boolean
}

export function SelectInput({
  label,
  description,
  errors,
  size = 'medium',
  value,
  onChange,
  children,
  disabled = false,
}: SelectInputProps) {
  const id = useId()
  return (
    <SelectField
      label={label}
      // Both ends of the association, from one generated identifier.
      elementId={id}
      labelFor={id}
      size={size}
      status={statusFor(errors)}
      errors={errors === undefined ? [] : [...errors]}
      {...(description === undefined ? {} : { description })}
    >
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(event: ChangeEvent<HTMLSelectElement>) =>
          onChange(event.target.value)
        }
      >
        {children}
      </select>
    </SelectField>
  )
}

export interface TextInputProps extends LabelledFieldProps {
  readonly value: string
  readonly onChange: (value: string) => void
  readonly placeholder?: string
  readonly type?: 'text' | 'search'
}

export function TextInput({
  label,
  description,
  errors,
  size = 'medium',
  value,
  onChange,
  placeholder,
  type = 'text',
}: TextInputProps) {
  const id = useId()
  return (
    <TextField
      label={label}
      // No `elementId` here: the text field takes only `labelFor`, so the generated
      // identifier is carried by the input below and pointed at from the label.
      labelFor={id}
      size={size}
      status={statusFor(errors)}
      errors={errors === undefined ? [] : [...errors]}
      {...(description === undefined ? {} : { description })}
    >
      <input
        id={id}
        type={type}
        value={value}
        {...(placeholder === undefined ? {} : { placeholder })}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          onChange(event.target.value)
        }
      />
    </TextField>
  )
}

export interface TextareaInputProps extends LabelledFieldProps {
  readonly value: string
  readonly onChange: (value: string) => void
  readonly rows?: number
  readonly required?: boolean
}

export function TextareaInput({
  label,
  description,
  errors,
  size = 'medium',
  value,
  onChange,
  rows = 4,
  required = false,
}: TextareaInputProps) {
  const id = useId()
  return (
    <TextareaField
      label={label}
      elementId={id}
      labelFor={id}
      size={size}
      status={statusFor(errors)}
      errors={errors === undefined ? [] : [...errors]}
      // Stated rather than implied. A field whose requirement is only discoverable by
      // submitting and being refused wastes the user's attempt to tell you something.
      indicator={required ? 'required' : 'optional'}
      {...(description === undefined ? {} : { description })}
    >
      <textarea
        id={id}
        rows={rows}
        value={value}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
          onChange(event.target.value)
        }
      />
    </TextareaField>
  )
}
